import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import type { NetworkDestination } from "../../../../db/schema";
import { sharedMutators } from "../../../../shared/mutators";
import { destinationSchema } from "../../../../shared/mutators/egressRouteShared";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { SegmentedControl } from "../../SegmentedControl";
import { Text } from "../../Text";
import { FieldLabel, ModalContent, ModalFooter } from "../shared";

type DestinationType = NetworkDestination["type"];
type RuleAction = "allow" | "deny";

export type EditingRule = {
  id: string;
  destinationType: DestinationType;
  destination: string;
  port: number | null;
  action: RuleAction;
};

export function RuleModal({
  editing,
  onClose,
}: {
  editing?: EditingRule;
  onClose: () => void;
}) {
  const z = useZero();
  const [type, setType] = useState<DestinationType>(
    editing?.destinationType ?? "hostnameGlob"
  );
  const [value, setValue] = useState(editing?.destination ?? "");
  const [action, setAction] = useState<RuleAction>(editing?.action ?? "allow");
  const [portText, setPortText] = useState(
    editing?.port != null ? String(editing.port) : ""
  );

  const trimmed = value.trim();
  const portTrimmed = portText.trim();
  const port = portTrimmed === "" ? null : Number(portTrimmed);
  const portValid =
    port === null || (Number.isInteger(port) && port >= 1 && port <= 65535);
  const destination: NetworkDestination | null =
    trimmed.length > 0
      ? type === "cidr"
        ? { type: "cidr", range: trimmed }
        : { type: "hostnameGlob", pattern: trimmed }
      : null;
  const destinationValid =
    destination !== null && destinationSchema.safeParse(destination).success;
  const canSave = destinationValid && portValid;

  const destinationHint =
    trimmed.length > 0 && !destinationValid && type === "cidr"
      ? "Enter a valid CIDR range with a prefix length, e.g. 10.0.0.0/16."
      : null;

  const save = () => {
    if (!canSave || !destination) return;
    if (editing) {
      z.mutate(
        sharedMutators.updateEgressRule({
          id: editing.id,
          destination,
          port,
          action,
        })
      );
    } else {
      z.mutate(
        sharedMutators.createEgressRule({
          id: nanoid(12),
          destination,
          port,
          action,
        })
      );
    }
    onClose();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalContent>
        <Text variant="text-regular-md">
          {editing ? "Edit rule" : "New rule"}
        </Text>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Action</Text>
          </Box>
          <div style={{ display: "flex" }}>
            <SegmentedControl
              options={["Allow", "Deny"] as const}
              value={action === "allow" ? "Allow" : "Deny"}
              onChange={(v) => setAction(v === "Allow" ? "allow" : "deny")}
            />
          </div>
        </FieldLabel>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Destination</Text>
          </Box>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <SegmentedControl
              options={["Hostname", "CIDR"] as const}
              tabWidth={80}
              value={type === "cidr" ? "CIDR" : "Hostname"}
              onChange={(v) => setType(v === "CIDR" ? "cidr" : "hostnameGlob")}
            />
            <Input
              autoFocus
              placeholder={
                type === "hostnameGlob" ? "*.openai.com" : "10.0.0.0/16"
              }
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                }
              }}
              style={{ flex: 1, minWidth: 0 }}
            />
          </div>
          {destinationHint && (
            <Box horizontal={8} top={6}>
              <Text variant="text-small" color="var(--text-error)">
                {destinationHint}
              </Text>
            </Box>
          )}
        </FieldLabel>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Port</Text>
          </Box>
          <Input
            placeholder="Any"
            value={portText}
            onChange={(e) => setPortText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              }
            }}
            style={{ minWidth: 0, width: "100%" }}
          />
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              Leave blank to match any port.
            </Text>
          </Box>
        </FieldLabel>

        <div style={{ height: 8 }} />
        <ModalFooter>
          {editing && (
            <Button size="l" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button size="l" onClick={save} disabled={!canSave}>
            {editing ? "Save" : "Add rule"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
